import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DossierEintrag } from '@models/dossier/dossier-models';
import { Merkmal, MerkmalLock } from '@models/merkmalbaum';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { BatchService } from 'app/core/services/batch/batch.service';
import { CustomWindowService } from 'app/core/services/custom-window-service/custom-window.service';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import { ValidationService } from 'app/core/services/validation/validation.service';
import { DossierSelectors } from 'app/modules/dossier-area/pages/dossier/store';
import * as DossierActions from 'app/modules/dossier-area/pages/dossier/store/dossier.actions';
import { DossierService } from 'app/modules/dossier-area/services/dossier/dossier.service';
import * as MerkmalActions from 'app/modules/shared-area/ducks/merkmal/store';
import { getLastSelectedMerkmal } from 'app/modules/shared-area/ducks/merkmal/store';
import * as PanelActions from 'app/modules/shared-area/ducks/panel/store/panel.actions';
import { SachverhaltCreateComponent } from 'app/modules/shared-area/entry-components/sachverhalt/sachverhalt-create/sachverhalt-create.component';
import { SelectMerkmalDialogComponent } from 'app/modules/shared-area/entry-components/select-merkmal-dialog/select-merkmal-dialog.component';
import { IAppState } from 'app/modules/shared-area/root-state';
import { findIndex, every } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, finalize, first, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as SachverhaltActions from './sachverhalt.actions';
import { DossierHelperService } from 'app/modules/dossier-area/services/dossier-helper/dossier-helper.service';
import { noOp } from 'app/core/utils/noOp';
import { SmoothScrollService } from 'app/core/services/smooth-scroll';
import { getTriggerElement } from './sachverhalt.selectors';

@Injectable()
export class SachverhaltEffects {
  constructor(
    private actions$: Actions,
    private dossierHelperService: DossierHelperService,
    private dossierService: DossierService,
    private i18n: I18nService,
    private validationService: ValidationService,
    private windowService: CustomWindowService,
    private batchService: BatchService,
    private store: Store<IAppState>,
    private smoothScrollService: SmoothScrollService,
    @Inject(DOCUMENT) private document: any,
  ) {}

  DossierSachverhaltLoeschenBeantragen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.dossierSachverhaltLoeschenBeantragen),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(payload =>
        this.dossierService.DossierSachverhaltLoeschenBeantragen(payload.id, payload.sachverhaltId).pipe(
          withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
          switchMap(([partialDossier, dossier]) => [
            DossierActions.partialUpdateDossier({
              updatedDossier: this.dossierHelperService.updateDossier(dossier, partialDossier),
            }),
          ]),
          tap(() => this.windowService.close()),
          catchError(error => of(SachverhaltActions.dossierSachverhaltLoeschenBeantragenError())),
          finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
        ),
      ),
    )
  );

  dossierBearbeitenSachverhaltAenderungenVerwerfen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.dossierBearbeitenSachverhaltAenderungenVerwerfen),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(payload =>
        this.dossierService.DossierBearbeitenSachverhaltAenderungenVerwerfen(payload.id, payload.sachverhaltId).pipe(
          withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
          switchMap(([partialDossier, dossier]) => [
            DossierActions.partialUpdateDossier({
              updatedDossier: this.dossierHelperService.updateDossier(dossier, partialDossier),
            }),
          ]),
          tap(() => this.windowService.close()),
          catchError(error =>
            of(SachverhaltActions.dossierBearbeitenSachverhaltAenderungenVerwerfenError({ payload: error })),
          ),
          finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
        ),
      ),
    )
  );

  DossierNeuenSachverhaltLoeschen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.dossierNeuenSachverhaltLoeschen),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(({ id, sachverhaltId }) =>
        this.dossierService.DossierNeuenSachverhaltLoeschen(id, sachverhaltId).pipe(
          withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
          switchMap(([partialDossier, dossier]) => [
            DossierActions.partialUpdateDossier({
              updatedDossier: this.dossierHelperService.removeFactFromDossier(dossier, partialDossier, sachverhaltId),
            }),
          ]),
          tap(() => this.windowService.close()),
          catchError(error => of(SachverhaltActions.dossierNeuenSachverhaltLoeschenError())),
          finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
        ),
      ),
    )
  );

  openSelectMerkmalDialog$ = createEffect(() => 
    this.actions$.pipe(
      ofType(SachverhaltActions.openSelectMerkmalDialog),
      withLatestFrom(this.store.pipe(select(getLastSelectedMerkmal))),
      tap(([action, lastMerkmal]) => {
        let dossier = null;
        let dossierType = null;
        this.store
          .pipe(
            select(DossierSelectors.getDossier),
            withLatestFrom(this.store.pipe(select(DossierSelectors.getDossierType))),
            tap(([dossierEntry, dossierTypeEntry]) => {
              dossier = dossierEntry;
              dossierType = dossierTypeEntry;
            }),
            first(),
          )
          .subscribe();
        const locks: MerkmalLock[] = dossier && dossier.locks && dossier.locks.attributeLocks ? dossier.locks.attributeLocks : [];

        this.windowService.open({
          title: this.i18n.getLocalizedString('dialog.addFactTitle'),
          content: SelectMerkmalDialogComponent,
          width: 750,
          triggerElement: action.triggerElement,
        });

        const selectMerkmalDialog: SelectMerkmalDialogComponent = this.windowService.getWindow().content.instance;
        // show only 2 levels of the tree
        selectMerkmalDialog.limitLevel = 2;
        // select last merkmal in tree
        selectMerkmalDialog.merkmal = lastMerkmal;
        selectMerkmalDialog.merkmalLocks = locks;
        selectMerkmalDialog.filterByLieferant = true;
        selectMerkmalDialog.allowedDossierType = dossierType;
        selectMerkmalDialog.excludeRoot = true;
        selectMerkmalDialog.selectMerkmal
          .pipe(
            // only possible to click on type Merkmal
            filter((merkmal: Merkmal) => merkmal.type === 'Merkmal'),

            // is merkmal locked by another user?
            filter(merkmal => findIndex(locks, lock => lock.merkmalId === merkmal.id) === -1),
            map(merkmal => merkmal.key),
            // save last merkmal in store
            tap(key => this.store.dispatch(MerkmalActions.setSelectedMerkmal({ lastSelectedMerkmal: key }))),
            // reset previous edit set (used for duplicate merkmale)
            tap(() => this.store.dispatch(SachverhaltActions.loadSachverhaltEditSet({ sachverhaltEdit: { sachverhaltId: null, erfassungSachverhaltId: null } }))),
            // close select merkmal dialog and open create sachverhalt window
            tap(() => {
              this.windowService.close();
              this.store.dispatch(SachverhaltActions.openCreateSachverhaltDialog({ triggerElement: action.triggerElement }));
            }),
            first(),
          )
          .subscribe();   
      }),
      map(() => noOp()) // <-- Always emit an action!
    ), 
    { dispatch: false }
  );

  openCreateSachverhaltDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.openCreateSachverhaltDialog),
      tap((action) => {
        this.windowService.open({
          title: this.i18n.getLocalizedString('dialog.createSachverhaltTitle'),
          content: SachverhaltCreateComponent,
          width: 750,
          triggerElement: action.triggerElement,
        });
      })
    ),
    { dispatch: false }
  );

  createSachverhaltAndOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.createSachverhaltAndOpen),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(sachverhalt =>
        this.dossierService
          .DossierNeuenSachverhaltErstellen(sachverhalt.Id, sachverhalt.Merkmal, sachverhalt.Values)
          .pipe(
            withLatestFrom(
              this.store.pipe(select(DossierSelectors.getDossier)),
              this.store.pipe(select(getTriggerElement))
            ),
            switchMap(([partialDossier, dossier, triggerElement]) => [
              DossierActions.partialUpdateDossier({
                updatedDossier: this.dossierHelperService.updateDossier(dossier, partialDossier),
              }),
              SachverhaltActions.openSelectMerkmalDialog({ triggerElement }),
            ]),
            tap(() => this.windowService.close()),
            catchError(error => 
              of(SachverhaltActions.setServerValidationError({ serverValidationErrors: error }))
            ),
            finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
          ),
      ),
    )
  );

  createSachverhaltAndClose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.createSachverhaltAndClose),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(sachverhalt =>
        this.dossierService
          .DossierNeuenSachverhaltErstellen(sachverhalt.Id, sachverhalt.Merkmal, sachverhalt.Values)
          .pipe(
            withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
            switchMap(([partialDossier, dossier]) => {
              this.windowService.close();
              const updatedDossier = this.dossierHelperService.updateDossier(dossier, partialDossier);
              
              // Open panels and scroll to attributes
              const dossierEintrag: DossierEintrag = partialDossier.items[0] as any;
              const anchor = this.dossierHelperService.getFullDossierMerkmalId(dossierEintrag)[0];
              const factId = this.dossierHelperService.getFactIds(dossierEintrag)[0];
              const panelsToOpen = DossierHelperService.getOpenPanelsFromAnchorKeys(anchor);
              panelsToOpen.forEach(panel => this.store.dispatch(PanelActions.showPanel({ selector: panel })));

              // Wait for all panels to be open, then scroll
              const panelsToOpen$: Observable<boolean>[] = [...panelsToOpen].map(panel =>
                this.store.pipe(select(DossierSelectors.panelIsRenderedSelector(panel))),
              );
              
              if (panelsToOpen$.length > 0) {
                combineLatest(panelsToOpen$)
                  .pipe(
                    filter(results => every(results, result => !!result)),
                    first(),
                    tap(() => {
                      setTimeout(() => {
                        const element = this.document.getElementById(factId);
                        this.smoothScrollService.scroll({
                          document: this.document,
                          scrollTarget: element,
                          scrollContainer: this.document.querySelector('main'),
                          duration: 1000,
                          scrollOffset: 150,
                        });
                      }, 0);
                    }),
                  )
                  .subscribe();
              }

              return [
                DossierActions.partialUpdateDossier({
                  updatedDossier,
                }),
                SachverhaltActions.createSachverhaltAndCloseSuccess(),
              ];
            }),
            catchError(error => {
              if (error.reason !== 'DuplicateCopy') {
                const ruleValidationError = this.validationService.getRuleValidationFailedError(error);
                return of(SachverhaltActions.setServerValidationError({
                  serverValidationErrors: this.validationService.getServerValidationErrorsFromResponse(ruleValidationError)
                }));
              }
              return of(SachverhaltActions.createSachverhaltAndCloseSuccess());
            }),
            finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
          ),
      ),
    )
  );

  loadSachverhalt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.loadSachverhaltEffect),
      map(action => action.param),
      switchMap(sachverhalt =>
        this.dossierService.DossierNeuenSachverhaltErstellenAnzeigen(sachverhalt.id, sachverhalt.merkmal).pipe(
          map(data => SachverhaltActions.loadSachverhaltSuccess({ sachverhalt: data })),
          catchError(error => of(SachverhaltActions.loadSachverhaltError())),
        ),
      ),
    )
  );
  
  speichereSachverhaltInDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.speichereSachverhaltInDossier),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(sachverhaltBearbeiten =>
        this.dossierService
          .DossierSachverhaltBearbeiten(
            sachverhaltBearbeiten.Id,
            sachverhaltBearbeiten.SachverhaltId,
            sachverhaltBearbeiten.Values,
          )
          .pipe(
            withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
            map(([partialDossier, dossier]) =>
              DossierActions.partialUpdateDossier({
                updatedDossier: this.dossierHelperService.updateDossier(dossier, partialDossier),
              }),
            ),
            tap(() => this.windowService.close()),
            catchError(error => 
              of(SachverhaltActions.setServerValidationError({ serverValidationErrors: error }))
            ),
            finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
          ),
      ),
    )
  );

  bearbeiteNeuenSachverhaltInDossier$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SachverhaltActions.bearbeiteNeuenSachverhaltInDossier),
      tap(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: true }))),
      map(action => action.payload),
      switchMap(sachverhalt =>
        this.dossierService
          .DossierNeuenSachverhaltBearbeiten(sachverhalt.Id, sachverhalt.ErfassungSachverhaltId, sachverhalt.Values)
          .pipe(
            withLatestFrom(this.store.pipe(select(DossierSelectors.getDossier))),
            map(([partialDossier, dossier]) =>
              DossierActions.partialUpdateDossier({
                updatedDossier: this.dossierHelperService.updateDossier(dossier, partialDossier),
              }),
            ),
            tap(() => this.windowService.close()),
            catchError(error => 
              of(SachverhaltActions.setServerValidationError({ serverValidationErrors: error }))
            ),
            finalize(() => setTimeout(() => this.store.dispatch(DossierActions.showSmallLoadingSpinner({ isInProgress: false })), 0)), // after rendering in the next tick
          ),
      ),
    )
  );
}
